import { Carousel, Icon } from '@byted/serial-components-i18n';
import Header from '@components/mobile-header';
import ScrollContainer from '@components/scroll-container';
import React, { useMemo, useRef, useState } from 'react';
import AcademyCarousel from '../component/academy-carousel';
import Indicator from '../component/indicator';
import cs from 'classnames';
import Panel from './panel';
import HomeApp from '@containers/home/h5/download';
import Footer from '@components/footer';
import { getBackground, getTime, loadData, useAcademy, useActivity, useNotice } from '../utils';
import { useLoadData } from '@utils/loadData';
import { IDConfigType } from '../type';
import {
    AdminPathH5,
    getArticleId,
    jumpUrl,
    Tea,
    useGoAdmin,
    useHeaderTheme,
    useHome,
    useNav,
    useScrollTrigger,
    useUserInfo,
} from '@utils/index';
import { getIdConfigList } from '@service/api';
import { initHomeData } from '@redux/actions/home';
import { useTranslation } from '@ies/intl-react-plugin';
import { dayjs } from '@utils/day';
import Loading from '@components/loading';
import { HeaderTheme } from '@constants/enum';
import { useAccount } from '@containers/account';
import BannerImg from '../images/banner-h5.png';
import { EVENTS } from '@constants/event';
import { EnterAcademyDetailSource } from '@constants/academy';
import { BgStyleList, useCopyright } from '../pc/components/copyright/utils';

import './index.less';

const getDefaultTime = (stamp: number | string) => dayjs.utc(Number(stamp) * 1000).format('YYYY-MM-DD HH:MM');

const HomeID = () => {
    const homeRef = useRef<HTMLDivElement>(null);
    const academyRef = useRef<HTMLDivElement>(null);
    const { serverInited = false, top_banner, top_author = [], rank_popular, rank_recent, rank_youth } = useHome();
    const activity = useActivity();
    const notices = useNotice(3);
    const academy = useAcademy(6);
    const goAdmin = useGoAdmin();
    const { id } = useUserInfo();
    const { copyrightList, loading: copyrightLoading } = useCopyright();
    const { handleWrite, getAccountPopup } = useAccount(true);

    const [hasShowDetail, setHasShowDetail] = useState(false);

    const navTo = useNav();

    const { t } = useTranslation();

    useLoadData<IDConfigType[]>({
        serverInited,
        request: getIdConfigList,
        action: data => initHomeData(data, true),
    });

    const headerInfo = useHeaderTheme({
        ref: homeRef,
        getModules: () => Array.from(homeRef.current!.querySelectorAll('section')),
        pageThemes: [
            HeaderTheme.Black,
            HeaderTheme.Black,
            HeaderTheme.Black,
            HeaderTheme.Black,
            HeaderTheme.Black,
            HeaderTheme.White,
            HeaderTheme.Black,
            HeaderTheme.Black,
        ],
        pageBgs: ['transparent', '#fff', '#fff', '#fff', '#fff', '#000', '#fff', '#fff'],
        getHeaderHeight: () => 80,
    });

    const loading = !serverInited || activity.loading || notices.loading || academy.loading || copyrightLoading;

    useScrollTrigger(
        [
            {
                el: '#home_author',
                onEnter: () => {
                    Tea('show_top_author');
                },
            },
            {
                el: '#home_copyright',
                onEnter: () => {
                    Tea('show_publishing_module');
                },
            },
            {
                el: '#home_rank',
                onEnter: () => {
                    Tea('show_author_list');
                },
            },
        ],
        () => window.innerHeight / 2,
    );

    const ranks = useMemo(
        () => [
            {
                title: t('pc_top_author_list'),
                data: rank_popular,
            },
            {
                title: t('pc_top_male_author_list'),
                data: rank_recent,
            },
            {
                title: t('pc_top_new_author_list'),
                data: rank_youth,
            },
        ],
        [rank_popular, rank_recent, rank_youth],
    );

    const handleClickNotice = () => {
        Tea('enter_annoucement');
        if (!id) {
            handleWrite(() => goAdmin(AdminPathH5.Announcement));
            return;
        }
        goAdmin(AdminPathH5.Announcement);
    };

    const handleGoCopyright = () => {
        Tea('click_publishing_detail');
        location.href = `${window.location.origin}/copyright`;
    };

    return (
        <div className="home-id" ref={homeRef}>
            {!loading ? (
                <>
                    <Header {...headerInfo} />
                    <section className="home-id-carousel">
                        <Carousel
                            autoPlay={{ hoverToPause: false }}
                            animation="fade"
                            showArrow="never"
                            onChange={index => Tea('slide_banner', { pos: index })}
                        >
                            <div
                                className="home-id-carousel-item"
                                onClick={() => {
                                    Tea('click_start_writing');
                                    handleWrite();
                                }}
                            >
                                <img src={BannerImg} alt="" />
                            </div>
                            {top_banner?.map((item, index) => (
                                <div
                                    key={index}
                                    className="home-id-carousel-item"
                                    style={{ width: '100%' }}
                                    onClick={() => jumpUrl(item.link)}
                                >
                                    <img src={item.image_url_h5} />
                                </div>
                            ))}
                        </Carousel>
                    </section>
                    <section className="home-id-activity">
                        <div
                            className="home-id-header"
                            onClick={() => {
                                Tea('enter_activity');
                                navTo('/reward');
                            }}
                        >
                            <h1>{t('massage_activity')}</h1>
                            <span className="home-id-arrow" />
                        </div>
                        <div className="home-id-activity-content">
                            {activity.data.map((item, index) => (
                                <div
                                    className="home-id-activity-content-item"
                                    key={index}
                                    onClick={() => {
                                        Tea('enter_activity', { url: item.activity_link });
                                        jumpUrl(item.activity_link);
                                    }}
                                >
                                    <img src={item.picture_link} alt="" />
                                    <div className="activity-title">{item.name}</div>
                                    <div className="home-id-divider" />
                                    <div className="home-id-card-bottom">
                                        <div className="home-id-card-bottom-time">
                                            {getTime(item.start_time)} - {getTime(item.end_time)}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                    <section className="home-id-notice">
                        <div className="home-id-header" onClick={handleClickNotice}>
                            <h1>{t('home_announcement')}</h1>
                            <span className="home-id-arrow" />
                        </div>
                        <div className="home-id-notice-content">
                            {notices.data.map((item, index) => (
                                <div className="home-id-notice-content-item" key={index}>
                                    <div className="notice-title">{item.title}</div>
                                    <div className="notice-desc">{item.abstract}</div>
                                    <div className="home-id-divider" />
                                    <div className="home-id-card-bottom">
                                        <div className="home-id-card-bottom-time">
                                            {getDefaultTime(item.announcement_time)}
                                        </div>
                                        <div
                                            className="home-id-card-bottom-detail"
                                            onClick={() => {
                                                Tea('enter_annoucement', { url: item.link_url });
                                                jumpUrl(item.link_url);
                                            }}
                                        >
                                            {t('payment_details')}
                                            <Icon type="link-arrow" />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                    <section className="home-id-academy" ref={academyRef} id="home_academy">
                        <div className="home-id-header">
                            <h1>{t('pc_academy')}</h1>
                            {/* <span className="home-id-arrow" onClick={() => goAdmin(AdminPathH5.Academy)} /> */}
                        </div>
                        <AcademyCarousel
                            data={academy.leftCards || []}
                            isInited={!academy.loading}
                            trigger="#home_academy"
                            isMobile
                            onClick={item => {
                                Tea(EVENTS.CLICK_ACADEMY_DETAIL, {
                                    channel: EnterAcademyDetailSource.H5HomeRecommend,
                                    article_id: getArticleId(item.link),
                                });
                            }}
                        />
                        <div className="home-id-academy-content">
                            {academy.rightCards.map((item, index) => (
                                <div className="home-id-academy-content-item" key={index}>
                                    <div className="academy-top">
                                        <img src={item.thumb_uri} alt="" />
                                        <div className="academy-detail-msg">
                                            <div className="academy-title">{item.title}</div>
                                            <div className="academy-desc">{item.desc}</div>
                                        </div>
                                    </div>
                                    <div className="home-id-divider" />
                                    <div className="home-id-card-bottom">
                                        <div className="home-id-card-bottom-time">
                                            {getDefaultTime(item.publish_time)}
                                        </div>
                                        <div
                                            className="home-id-card-bottom-detail"
                                            onClick={() => {
                                                Tea('enter_academy', { url: item.link_url });
                                                Tea(EVENTS.CLICK_ACADEMY_DETAIL, {
                                                    channel: EnterAcademyDetailSource.H5HomeRecommend,
                                                    article_id: getArticleId(item.link_url),
                                                });
                                                jumpUrl(item.link_url);
                                            }}
                                        >
                                            {t('payment_details')}
                                            <Icon type="link-arrow" />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>
                    <section className="home-id-author" id="home_author">
                        <h1>{t('pc_star_writer')}</h1>
                        <ScrollContainer
                            renderIndicator={current => (
                                <Indicator current={current} total={top_author.length} isMobile />
                            )}
                        >
                            {top_author.map((item, index) => (
                                <div
                                    className="home-id-author-card"
                                    key={index}
                                    style={{ backgroundImage: `url(${item.book_cover})` }}
                                >
                                    <div
                                        className="home-id-author-card-content"
                                        style={{ background: getBackground(item.cover_color) }}
                                    >
                                        {Number(item.is_top) ? (
                                            <img className="author-avatar" src={item.avatar} alt="" />
                                        ) : null}
                                        <div className="author-name">{item.author}</div>
                                        <div className="author-desc">{item.desc}</div>
                                    </div>
                                </div>
                            ))}
                        </ScrollContainer>
                        <div className="home-id-author-tag">{t('pc_top_author')}</div>
                    </section>
                    <section
                        className={cs('home-id-copyright', { 'no-show-detail': !hasShowDetail })}
                        id="home_copyright"
                    >
                        <div className="home-id-copyright-title">{t('publishing_module_name')}</div>
                        <div className="home-id-copyright-subtitle">{t('publishing_module_name_second')}</div>
                        {copyrightList.map((item, index) => {
                            return (
                                <div
                                    key={item.book_name}
                                    className="home-id-copyright-item"
                                    style={{ background: BgStyleList[index] }}
                                >
                                    <div className="home-id-copyright-img-container">
                                        <span className="home-id-copyright-img-line" />
                                        <img src={item.book_cover} className="home-id-copyright-img" />
                                    </div>
                                    <div className="home-id-copyright-extra">
                                        <div className="home-id-copyright-extra-book-name">{item.book_name}</div>
                                        <div className="home-id-copyright-extra-author">
                                            <img
                                                className="home-id-copyright-extra-author-avatar"
                                                src={item.author_avatar}
                                            />
                                            <div className="home-id-copyright-extra-author-name">
                                                {item.author_name}
                                            </div>
                                        </div>
                                        <div className="home-id-copyright-extra-introduction">
                                            {item.book_introduction}
                                        </div>
                                        <div className="home-id-copyright-extra-publish-container">
                                            <img
                                                className="home-id-copyright-extra-logo"
                                                src={item.publish_house_logo}
                                            />
                                            <div className="home-id-copyright-extra-publish-divider" />
                                            <div className="home-id-copyright-extra-publish">{item.has_published}</div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        {!hasShowDetail && (
                            <div className="home-id-copyright-show-detail" onClick={() => setHasShowDetail(true)}>
                                <div className="home-id-copyright-show-detail-bg" />
                                <div className="home-id-copyright-show-detail-footer">
                                    <div className="home-id-copyright-show-detail-footer-info">{t('detail_label')}</div>
                                    <div className="home-id-copyright-show-detail-footer-icon" />
                                </div>
                            </div>
                        )}
                        <div className="home-id-copyright-go-detail-container">
                            <div className="home-id-copyright-go-detail" onClick={handleGoCopyright}>
                                <div className="home-id-copyright-go-detail-info">{t('published_book_detail')}</div>
                                <div className="home-id-copyright-go-detail-icon" />
                            </div>
                        </div>
                    </section>
                    <section className="home-id-rank" id="home_rank">
                        <ScrollContainer
                            renderIndicator={current => (
                                <div className="home-id-rank-indicator">
                                    {ranks.map((_, index) => (
                                        <span
                                            className={cs('home-id-rank-indicator-item', { active: current === index })}
                                            key={index}
                                        />
                                    ))}
                                </div>
                            )}
                        >
                            {ranks.map(item => (
                                <Panel data={item.data || []} title={item.title} key={item.title} />
                            ))}
                        </ScrollContainer>
                    </section>
                    <div className="home">
                        <HomeApp />
                    </div>
                    <Footer />
                    {getAccountPopup()}
                </>
            ) : (
                <Loading />
            )}
        </div>
    );
};

HomeID.loadData = async (ctx, store) => loadData(ctx, store, true);

export default HomeID;
